// OnDutyIndex.jsx
import React, { useEffect, useState } from "react";
import {
  Grid,
  CssBaseline,
  AppBar,
  Toolbar,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  TextField,
  TableContainer,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { Close, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import AddUpdateOnDuty from "./AddUpdateOnDuty";
import dayjs from "dayjs";

const OnDutyIndex = ({ handleBackWinderMachineEntry }) => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  /* ---------- Menu Filter ---------- */
  const [selectedOption, setSelectedOption] = useState("All Entries");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuItemClick = (option) => {
    setSelectedOption(option);
    handleMenuClose();
  };

  /* ---------- Column Filters ---------- */
  const [filters, setFilters] = useState({
    id_on_duty: "",
    id_employee: "",
    travel_purpose: "",
    location: "",
    on_duty_from_date: "",
    on_duty_to_date: "",
    approved_by: "",
    billing_id: "",
    company_id: "",
    plant_id: "",
    status: "",
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  /* ---------- Load List ---------- */
  const loadList = () => {
    const payload = {
      p_id_employee: 3,
      p_company_id: localStorage.getItem("companyid"),
      p_plant_id: 1,
      p_id_on_duty: 0,
    };

    callApi(
      API_ENDPOINTS.onduty?.list || "/api/onduty/list",
      "POST",
      { "Content-Type": "application/json" },
      payload
    )
      .then((data) => {
        const safeData = Array.isArray(data) ? data : [];
        setList(safeData);
        setFiltered(safeData);
      })
      .catch(() => {
        setList([]);
        setFiltered([]);
      });
  };

  useEffect(() => {
    loadList();
  }, []);

  /* ---------- Apply Menu + Column Filters ---------- */
  useEffect(() => {
    let data = [...list];

    /* ---- Menu Filter (STATUS STRING ONLY) ---- */
    if (selectedOption === "Approved Entries") {
      data = data.filter(
        (x) => (x.status || "").toLowerCase() === "approved"
      );
    }

    if (selectedOption === "Pending Entries") {
      data = data.filter(
        (x) => (x.status || "").toLowerCase() === "pending"
      );
    }

    /* ---- Column-wise Filters ---- */
    data = data.filter((row) =>
      Object.keys(filters).every((key) => {
        if (!filters[key]) return true;

        return String(row[key] ?? "")
          .toLowerCase()
          .includes(filters[key].toLowerCase());
      })
    );

    setFiltered(data);
  }, [list, filters, selectedOption]);

  /* ---------- Edit ---------- */
  const handleEdit = async (row) => {
    try {
      const payload = {
        p_id_on_duty: row.id_on_duty,
        p_company_id: localStorage.getItem("companyid"),
        p_plant_id: 1,
        p_id_employee: row.id_employee,
      };

      const detailData = await callApi(
        API_ENDPOINTS.onduty.ApprovallistgetById,
        "POST",
        { "Content-Type": "application/json" },
        payload
      );

      setFormData(detailData);
      setIsFormOpen(true);
    } catch (err) {
      console.error("Failed to fetch on-duty details:", err);
    }
  };

  const handleAddNew = () => {
    setFormData(null);
    setIsFormOpen(true);
  };

  const handleExit = () => {
    handleBackWinderMachineEntry();
  };

  return (
    <Grid>
      {!isFormOpen && (
        <>
          <CssBaseline />
          <AppBar
            position="static"
            sx={{
              backgroundColor: "inherit",
              height: "48px",
              justifyContent: "center",
            }}
          >
            <Toolbar>
              <Typography variant="h6">
                On Duty / Travel Requests
              </Typography>

              <Button
  color="secondary"
  onClick={handleMenuClick}
  endIcon={
    <ExpandMoreIcon sx={{ color: "teal", fontSize: "18px" }} />
  }
  sx={{
    ml: 2,
    textTransform: "none",
    fontSize: "16px",
    fontWeight: 500,
  }}
>
  {selectedOption}
</Button>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleMenuItemClick("All Entries")}>
                  All Entries
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("Approved Entries")}
                >
                  Approved Entries
                </MenuItem>
                <MenuItem
                  onClick={() => handleMenuItemClick("Pending Entries")}
                >
                  Pending Entries
                </MenuItem>
              </Menu>

           <Button
  size="small"
  variant="contained"
  color="secondary"
  sx={{
    marginLeft: "auto",
    textTransform: "none",
    fontWeight: 500,
  }}
  onClick={handleAddNew}
>
  New Request
</Button>

              <IconButton onClick={handleExit}>
                <Close />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            p={1}
            sx={{
              "& .MuiTableCell-root": { whiteSpace: "nowrap" },
              maxHeight: "75vh",
              overflowX: "auto",
              overflowY: "auto",
            }}
          >
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>On Duty ID</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Billing ID</TableCell>
                    <TableCell>Company ID</TableCell>
                    <TableCell>Plant ID</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>

                  {/* Column Filters */}
                  <TableRow>
                    {Object.keys(filters).map((col) => (
                      <TableCell key={col}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search..."
                          value={filters[col]}
                          onChange={(e) =>
                            handleFilterChange(col, e.target.value)
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id_on_duty}>
                      <TableCell
                        sx={{
                          color: "teal",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => handleEdit(row)}
                      >
                        {row.id_on_duty}
                      </TableCell>
                      <TableCell>{row.id_employee}</TableCell>
                      <TableCell>{row.travel_purpose}</TableCell>
                      <TableCell>{row.location}</TableCell>
                      {/* <TableCell>{row.on_duty_from_date}</TableCell>
                      <TableCell>{row.on_duty_to_date}</TableCell> */}
                      <TableCell>
  {row.on_duty_from_date
    ? dayjs(row.on_duty_from_date).format("DD-MMM-YYYY")
    : "-"}
</TableCell>

<TableCell>
  {row.on_duty_to_date
    ? dayjs(row.on_duty_to_date).format("DD-MMM-YYYY")
    : "-"}
</TableCell>
                      <TableCell>{row.approved_by}</TableCell>
                      <TableCell>{row.billing_id}</TableCell>
                      <TableCell>{row.company_id}</TableCell>
                      <TableCell>{row.plant_id}</TableCell>
                      <TableCell>{row.status || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}

      {isFormOpen && (
        <AddUpdateOnDuty
          formData={formData}
          setIsFormOpen={setIsFormOpen}
          reloadList={loadList}
        />
      )}
    </Grid>
  );
};

export default OnDutyIndex;
